import { skipToken, useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { Suspense, useEffect, useRef, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { PasswordConfirmField } from '@/components/sv/password-confirm-field'
import { ToasterContent } from '@/components/sv/toaster-content'
import { UserSummary } from '@/components/sv/user-summary'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { gql } from '@/lib/graphql/gql'
import {
  useIdCompany,
  usePasswordToken,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import {
  COMPANY_LEVEL_ROLE_IDS,
  PROJECT_LEVEL_ROLE_IDS,
  RoleID,
  getCapabilityScope,
  isCompanyDeveloper,
} from '@/lib/rbac'
import {
  PermissionForm,
  permissionSchema,
} from '@/routes/user-management/permission-form'
import { filterOutSiteRoles } from '@/routes/user-management/permission-form/filter-service-capabilities'
import { UpdateUserAccessFallback } from '@/routes/user-management/update-user-access-sheet/fallback'
import type {
  CompanyUserQuery,
  CompanyUserQueryVariables,
  SetProjectCapabilityMutation,
  SetProjectCapabilityMutationVariables,
  UpdateCapabilitiesMutation,
  UpdateCapabilitiesMutationVariables,
  UpdateCompanyRoleMutation,
  UpdateCompanyRoleMutationVariables,
} from '@/routes/user-management/update-user-access-sheet/index.types.gen'

const QUERY_COMPANY_USER = gql`
  query CompanyUser($idCompany: String!, $idUser: String!) {
    company(id: $idCompany) {
      id
      userByIdUser(idUser: $idUser) {
        id
        user {
          id
          email
          firstName
          lastName
          fullName
          image
        }
        capabilities {
          id
          idApp
          idDatabase
          idStaticSite
          idObjectStorage
          idLoadBalancer
          idProject
          idRole
          idUser
          idCompany
        }
      }
    }
  }
`

const UPDATE_COMPANY_ROLE = gql`
  mutation UpdateCompanyRole(
    $idUser: String!
    $idCompany: String!
    $idRole: String!
    $passwordToken: String
  ) {
    setCompanyCapabilityOfUser(
      idUser: $idUser
      idCompany: $idCompany
      idRole: $idRole
      passwordToken: $passwordToken
    )
  }
`

const UPDATE_CAPABILITIES = gql`
  mutation UpdateCapabilities(
    $idCompany: String!
    $idUsers: [String!]!
    $capabilities: [CapabilityInput!]!
  ) {
    editCapabilities(
      idCompany: $idCompany
      idUsers: $idUsers
      capabilities: $capabilities
    )
  }
`

const SET_PROJECT_CAPABILITY = gql`
  mutation SetProjectCapability(
    $idCompany: String!
    $idUser: String!
    $projectCapabilities: [ProjectCapabilityInput!]!
  ) {
    setProjectCapabilityOfUser(
      idCompany: $idCompany
      idUser: $idUser
      projectCapabilities: $projectCapabilities
    )
  }
`

const schema = permissionSchema
  .safeExtend({
    password: z.string().optional(),
  })
  .refine((values) => !(values.type === 'company' && !values.password), {
    message: 'Password is required',
    path: ['password'],
  })

type Props = {
  isOpen: boolean
  onClose: () => void
}

const projectRoles = [...PROJECT_LEVEL_ROLE_IDS]

const UpdateUserAccessSheetContent = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const { rolesInCompany } = useRBAC()
  const { idUser = '' } = useParams<{ idUser: string }>()
  const [isPending, startTransition] = useTransition()
  const isCompanyDeveloperViewer = isCompanyDeveloper(rolesInCompany)
  const blockedAccessRef = useRef(false)
  const { obtainPasswordToken } = usePasswordToken()

  const [updateCompanyRole] = useMutation<
    UpdateCompanyRoleMutation,
    UpdateCompanyRoleMutationVariables
  >(UPDATE_COMPANY_ROLE)
  const [updateCapabilities] = useMutation<
    UpdateCapabilitiesMutation,
    UpdateCapabilitiesMutationVariables
  >(UPDATE_CAPABILITIES)
  const [setProjectCapability] = useMutation<
    SetProjectCapabilityMutation,
    SetProjectCapabilityMutationVariables
  >(SET_PROJECT_CAPABILITY)

  const { data } = useSuspenseQuery<
    CompanyUserQuery,
    CompanyUserQueryVariables
  >(
    QUERY_COMPANY_USER,
    isOpen
      ? { variables: { idCompany, idUser }, fetchPolicy: 'no-cache' }
      : skipToken
  )

  const userCapabilities = data?.company?.userByIdUser?.capabilities ?? []
  const capabilityScope = getCapabilityScope(userCapabilities)
  const canCompanyDeveloperManageUser =
    capabilityScope === 'project' || capabilityScope === 'service'
  const isCompanyDeveloperBlocked =
    isOpen && isCompanyDeveloperViewer && !canCompanyDeveloperManageUser
  const allowedTypes: Array<'project' | 'service'> | undefined =
    isCompanyDeveloperViewer ? ['project', 'service'] : undefined

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: '',
      type: 'service',
      idRole: undefined,
      serviceType: 'All',
      capabilities: [],
      projectCapabilities: [],
    },
  })

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = form
  const isCompanyAccessUpdate =
    watch('type') === 'company' && !isCompanyDeveloperViewer

  useEffect(() => {
    if (isOpen) {
      reset({
        password: '',
        type: 'service',
        idRole: undefined,
        serviceType: 'All',
        capabilities: [],
        projectCapabilities: [],
      })
    }
  }, [isOpen, reset])

  useEffect(() => {
    if (data?.company?.userByIdUser) {
      const capabilities = data?.company?.userByIdUser?.capabilities ?? []
      const companyCapability = capabilities?.find((c) =>
        COMPANY_LEVEL_ROLE_IDS.includes(
          c.idRole as (typeof COMPANY_LEVEL_ROLE_IDS)[number]
        )
      )
      const projectCapabilitiesData = capabilities?.filter(
        (c) => c.idProject && projectRoles.includes(c.idRole)
      )
      const serviceCapabilities =
        capabilities?.filter(
          (c) =>
            !COMPANY_LEVEL_ROLE_IDS.includes(
              c.idRole as (typeof COMPANY_LEVEL_ROLE_IDS)[number]
            ) &&
            !projectRoles.includes(c.idRole) &&
            !c.idProject
        ) ?? []

      let type: 'company' | 'project' | 'service' = 'service'
      if (companyCapability) {
        type = 'company'
      } else if (projectCapabilitiesData?.length) {
        type = 'project'
      }

      reset({
        password: '',
        type,
        idRole: companyCapability?.idRole ?? RoleID.COMPANY_ADMIN,
        serviceType: 'All',
        capabilities: serviceCapabilities.map((c) => ({
          idRole: c.idRole ?? undefined,
          idApp: c.idApp ?? undefined,
          idDatabase: c.idDatabase ?? undefined,
          idObjectStorage: c.idObjectStorage ?? undefined,
          idStaticSite: c.idStaticSite ?? undefined,
          idLoadBalancer: c.idLoadBalancer ?? undefined,
        })),
        projectCapabilities: projectCapabilitiesData.map((c) => ({
          idProject: c.idProject as string,
          idRole: c.idRole ?? undefined,
        })),
      })
    }
  }, [data, reset])

  useEffect(() => {
    if (!isCompanyDeveloperBlocked) {
      blockedAccessRef.current = false
      return
    }

    if (blockedAccessRef.current) {
      return
    }

    blockedAccessRef.current = true
    toast(
      <ToasterContent variant="error">
        Company developers can only update project-level or service-level users
      </ToasterContent>,
      { position: 'top-right' }
    )
    onClose()
  }, [isCompanyDeveloperBlocked, onClose])

  const onSubmit = async (values: z.infer<typeof schema>) => {
    if (isPending || !isValid) {
      return
    }

    if (isCompanyDeveloperViewer && values.type === 'company') {
      toast(
        <ToasterContent variant="error">
          Company developers can only update project-level or service-level
          users
        </ToasterContent>,
        { position: 'top-right' }
      )
      return
    }

    startTransition(async () => {
      try {
        if (values.type === 'company' && !isCompanyDeveloperViewer) {
          const result = await obtainPasswordToken(values.password ?? '')
          if (result.error) {
            form.setError('password', { message: result.error })
            return
          }
          await updateCompanyRole({
            variables: {
              idCompany,
              idUser,
              idRole: values.idRole as string,
              passwordToken: result.passwordToken,
            },
          })
        }
        if (values.type === 'project') {
          const projectCaps =
            values.projectCapabilities?.map((c) => ({
              idProject: c.idProject,
              idRole: c.idRole ?? null,
            })) ?? []
          await setProjectCapability({
            variables: {
              idCompany,
              idUser,
              projectCapabilities: projectCaps,
            },
          })
        }
        if (values.type === 'service') {
          const capabilities = filterOutSiteRoles(values.capabilities ?? [])

          await updateCapabilities({
            variables: {
              idCompany,
              idUsers: [idUser],
              capabilities: capabilities as NonNullable<
                z.infer<typeof schema>['capabilities']
              >,
            },
          })
        }
        toast(
          <ToasterContent variant="success">
            User access updated successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to update user access
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  if (isCompanyDeveloperBlocked) {
    return null
  }

  return (
    <>
      <div className="px-4 overflow-y-auto">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <UserSummary
              email={data?.company?.userByIdUser?.user?.email}
              firstName={data?.company?.userByIdUser?.user?.firstName}
              fullName={data?.company?.userByIdUser?.user?.fullName}
              image={data?.company?.userByIdUser?.user?.image}
              lastName={data?.company?.userByIdUser?.user?.lastName}
            />
            <PermissionForm allowedTypes={allowedTypes} />
            {isCompanyAccessUpdate ? (
              <PasswordConfirmField
                description="Changing company-level access requires your Sevalla password."
                isDisabled={isPending}
              />
            ) : null}
            <Button type="submit" hidden />
          </form>
        </Form>
      </div>
      <SheetFooter className="sticky">
        <Button
          type="submit"
          disabled={isPending}
          onClick={handleSubmit(onSubmit)}
        >
          {isPending ? <Loader className="animate-spin" /> : null}
          Update access
        </Button>
      </SheetFooter>
    </>
  )
}

export const UpdateUserAccessSheet = ({ isOpen, onClose }: Props) => {
  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Update user access</SheetTitle>
        </SheetHeader>
        <Suspense fallback={<UpdateUserAccessFallback />}>
          <UpdateUserAccessSheetContent isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </SheetContent>
    </Sheet>
  )
}
